import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { CheckCircle, XCircle, Clock, Trash2, Eye, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YouthApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewApp, setViewApp] = useState(null);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(`${API_URL}/api/admin/youth-applications`, { params });
      setApplications(res.data.applications || []);
    } catch { toast.error('Failed to load applications'); }
    setLoading(false);
  };

  useEffect(() => { fetchApps(); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/youth-applications/${id}/status`, { status });
      toast.success(`Application ${status}`);
      setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      if (viewApp?.id === id) setViewApp(prev => ({ ...prev, status }));
    } catch { toast.error('Failed to update'); }
  };

  const deleteApp = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/youth-applications/${id}`);
      toast.success('Deleted');
      setApplications(prev => prev.filter(a => a.id !== id));
      if (viewApp?.id === id) setViewApp(null);
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const statusBadge = (s) => {
    if (s === 'approved') return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    if (s === 'rejected') return <Badge className="bg-red-500/10 text-red-600 border-red-500/20"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="youth-applications-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {applications.length} application{applications.length !== 1 ? 's' : ''}
            {pendingCount > 0 && <span className="text-amber-600 font-medium"> ({pendingCount} pending review)</span>}
          </p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No applications found.</div>
      ) : (
        <div className="space-y-3">
          {applications.map(app => (
            <Card key={app.id} className={app.status === 'pending' ? 'border-amber-500/30' : ''} data-testid={`app-${app.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-display font-bold text-lg">{app.athlete_name}</span>
                      {statusBadge(app.status)}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span>{app.city}, {app.province}</span>
                      <span>Age: {app.age || '—'}</span>
                      <span>Guardian: {app.guardian_name}</span>
                      <span>{formatDate(app.submitted_at)}</span>
                    </div>
                    {app.primary_events?.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {app.primary_events.map(ev => <Badge key={ev} variant="outline" className="text-xs">{ev}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setViewApp(app)} title="View details"><Eye className="w-4 h-4" /></Button>
                    {app.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => updateStatus(app.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white" title="Approve"><CheckCircle className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => updateStatus(app.id, 'rejected')} className="text-red-600 border-red-200 hover:bg-red-50" title="Reject"><XCircle className="w-4 h-4" /></Button>
                      </>
                    )}
                    {app.status !== 'pending' && (
                      <Button variant="outline" size="sm" onClick={() => updateStatus(app.id, 'pending')} title="Reset to pending"><Clock className="w-4 h-4" /></Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => deleteApp(app.id)} className="text-muted-foreground hover:text-red-500" title="Delete"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!viewApp} onOpenChange={() => setViewApp(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">{viewApp?.athlete_name}</DialogTitle>
          </DialogHeader>
          {viewApp && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">{statusBadge(viewApp.status)} <span className="text-sm text-muted-foreground">Submitted {formatDate(viewApp.submitted_at)}</span></div>

              <Section title="Athlete Information">
                <Row label="Name" value={viewApp.athlete_name} />
                <Row label="Date of Birth" value={viewApp.date_of_birth} />
                <Row label="Age" value={viewApp.age} />
                <Row label="Address" value={`${viewApp.mailing_address}, ${viewApp.city}, ${viewApp.province} ${viewApp.postal_code}`} />
                <Row label="Phone" value={viewApp.phone} />
                <Row label="Email" value={viewApp.email} />
              </Section>

              <Section title="Parent / Guardian">
                <Row label="Name" value={viewApp.guardian_name} />
                <Row label="Relationship" value={viewApp.guardian_relationship} />
                <Row label="Phone" value={viewApp.guardian_phone} />
                {viewApp.guardian_phone_secondary && <Row label="Phone 2" value={viewApp.guardian_phone_secondary} />}
                <Row label="Email" value={viewApp.guardian_email} />
                {viewApp.guardian_address && <Row label="Address" value={`${viewApp.guardian_address}, ${viewApp.guardian_city || ''} ${viewApp.guardian_province || ''} ${viewApp.guardian_postal_code || ''}`} />}
              </Section>

              <Section title="Membership">
                <Row label="Subscription Name" value={viewApp.subscription_name || '—'} />
                <Row label="Subscription Email" value={viewApp.subscription_email || '—'} />
              </Section>

              <Section title="Rodeo Participation">
                <Row label="Circuits / Associations" value={viewApp.rodeo_circuits || '—'} />
                <Row label="Planned Events" value={viewApp.planned_events_list || '—'} multiline />
                <Row label="Primary Events" value={viewApp.primary_events?.join(', ') || '—'} />
              </Section>

              <Section title="Apparel">
                <Row label="Shirt Size" value={viewApp.shirt_size || '—'} />
                <Row label="Hat Size" value={viewApp.hat_size || '—'} />
                {viewApp.apparel_notes && <Row label="Notes" value={viewApp.apparel_notes} />}
              </Section>

              <Section title="Signatures">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Athlete Signature</p>
                    {(viewApp.signature_athlete_url || viewApp.signature_athlete_data) && (
                      <img src={viewApp.signature_athlete_url || viewApp.signature_athlete_data} alt="Athlete Signature" className="h-16 bg-white rounded border p-1" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Parent/Guardian Signature</p>
                    {(viewApp.signature_guardian_url || viewApp.signature_guardian_data) && (
                      <img src={viewApp.signature_guardian_url || viewApp.signature_guardian_data} alt="Guardian Signature" className="h-16 bg-white rounded border p-1" />
                    )}
                  </div>
                </div>
              </Section>

              <div className="flex gap-2 justify-end pt-4 border-t">
                {viewApp.status === 'pending' && (
                  <>
                    <Button onClick={() => updateStatus(viewApp.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white rounded-full"><CheckCircle className="w-4 h-4 mr-2" />Approve</Button>
                    <Button variant="outline" onClick={() => updateStatus(viewApp.id, 'rejected')} className="text-red-600 rounded-full"><XCircle className="w-4 h-4 mr-2" />Reject</Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setViewApp(null)} className="rounded-full">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</h3>
    <div className="bg-muted/30 rounded-lg p-3 space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value, multiline }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1">
    <span className="text-xs text-muted-foreground w-32 flex-shrink-0">{label}</span>
    {multiline ? (
      <span className="text-sm whitespace-pre-wrap">{value}</span>
    ) : (
      <span className="text-sm">{value}</span>
    )}
  </div>
);

export default YouthApplicationsTab;
